import { useMemo } from "react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Line, ComposedChart,
} from "recharts";
import { Clock, TrendingUp, Grid3x3 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { PosCard } from "./PosTable";
import { EmptyState } from "@/components/common/EmptyState";
import { fmtCompact, fmtInt, fmtNumber } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;

function HeatMap({ matrix, branches, lang, t }) {
    const { rows, hours, max } = useMemo(() => {
        const hset = new Set();
        const byBranch = new Map();
        let mx = 0;
        for (const c of matrix || []) {
            hset.add(c.hour);
            if (!byBranch.has(c.pos_branch_id)) {
                byBranch.set(c.pos_branch_id, { pos_branch_id: c.pos_branch_id, name_ar: c.name_ar, name_en: c.name_en, cells: {} });
            }
            byBranch.get(c.pos_branch_id).cells[c.hour] = c;
            mx = Math.max(mx, c.gross || 0);
        }
        return { rows: [...byBranch.values()], hours: [...hset].sort((a, b) => a - b), max: mx };
    }, [matrix]);

    if (!rows.length) return <EmptyState compact title={t("no_data")} dataset="pos_branch_hourly" />;

    return (
        <div data-testid={PI.heatmap} className="overflow-x-auto" dir="ltr">
            <table className="w-full min-w-[720px] border-separate" style={{ borderSpacing: "2px" }}>
                <thead>
                    <tr>
                        <th className="sticky start-0 z-10 bg-[color:var(--bg-surface)] px-2 text-start text-[11px] font-semibold text-[color:var(--ink-muted)]">
                            {t("pi_col_branch")}
                        </th>
                        {hours.map((h) => (
                            <th key={h} className="text-center text-[10px] font-medium text-[color:var(--ink-muted)] tnum">
                                {String(h).padStart(2, "0")}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.pos_branch_id}>
                            <td className="sticky start-0 z-10 max-w-[140px] truncate bg-[color:var(--bg-surface)] px-2 text-[11px] font-medium text-[color:var(--ink)]">
                                {lang === "ar" ? r.name_ar || r.name_en : r.name_en || r.name_ar}
                            </td>
                            {hours.map((h) => {
                                const c = r.cells[h];
                                const ratio = c && max ? c.gross / max : 0;
                                return (
                                    <td key={h} data-testid={PI.heatCell(r.pos_branch_id, h)}
                                        title={c ? `${fmtInt(c.orders, lang)} · ${fmtNumber(c.gross, lang, 0)}` : "—"}
                                        className="h-7 rounded-[4px] text-center align-middle text-[9px] tnum"
                                        style={{
                                            backgroundColor: c ? `color-mix(in srgb, var(--brand-orange) ${Math.max(8, Math.round(ratio * 100))}%, transparent)` : "var(--bg-surface-2)",
                                            color: ratio > 0.55 ? "#fff" : "var(--ink-muted)",
                                        }}>
                                        {c ? fmtCompact(c.gross) : ""}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function PosHourlyPanel({ data }) {
    const { t, lang } = useI18n();
    const hourly = (data?.hourly || []).map((h) => ({ ...h, label: String(h.hour).padStart(2, "0") }));
    const series = (data?.series || []).map((s) => ({ ...s, label: s.date.slice(5) }));

    return (
        <div className="grid grid-cols-1 gap-4">
            <PosCard icon={Clock} title={t("pi_hourly_title")} desc={t("pi_hourly_desc")} testId={PI.hourlyChart}>
                {hourly.length ? (
                    <div dir="ltr" style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <ComposedChart data={hourly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--ink-muted)" }} />
                                <YAxis yAxisId="l" tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} />
                                <YAxis yAxisId="r" orientation="right" tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} />
                                <Tooltip
                                    formatter={(v, n) => [fmtNumber(v, lang, n === t("pi_col_orders") ? 0 : 2), n]}
                                    contentStyle={{ borderRadius: 12, border: "1px solid var(--border-soft)", fontSize: 12 }}
                                />
                                <Bar yAxisId="l" dataKey="gross" name={t("pi_col_revenue")} fill="var(--brand-orange)" radius={[4, 4, 0, 0]} />
                                <Line yAxisId="r" type="monotone" dataKey="orders" name={t("pi_col_orders")} stroke="var(--brand-brown)" strokeWidth={2} dot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                ) : <EmptyState compact title={t("no_data")} dataset="pos_branch_hourly" />}
            </PosCard>

            <PosCard icon={Grid3x3} title={t("pi_heatmap_title")} desc={t("pi_heatmap_desc")}>
                <HeatMap matrix={data?.hourly_matrix} branches={data?.branches} lang={lang} t={t} />
            </PosCard>

            <PosCard icon={TrendingUp} title={t("pi_kpi_gross")} desc={t("pi_kpi_avg_ticket")} testId={PI.trendChart}>
                {series.length ? (
                    <div dir="ltr" style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer>
                            <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--ink-muted)" }} />
                                <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} />
                                <Tooltip
                                    formatter={(v, n) => [fmtNumber(v, lang, 2), n]}
                                    contentStyle={{ borderRadius: 12, border: "1px solid var(--border-soft)", fontSize: 12 }}
                                />
                                <Bar dataKey="in_store_total" stackId="a" name={t("pi_kpi_in_store")} fill="var(--brand-brown)" />
                                <Bar dataKey="apps_total" stackId="a" name={t("pi_kpi_apps")} fill="var(--brand-orange)" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : <EmptyState compact title={t("no_data")} dataset="pos_branch_daily" />}
            </PosCard>
        </div>
    );
}
